<template>
  <div class="app-frame-container">
    <!-- 顶部导航栏（始终显示，提供返回功能） -->
    <div class="header-bar">
      <div class="back-button" @click="goBack">
        <i class="back-icon"></i>
        <span>返回</span>
      </div>
      <div class="header-title">页面详情</div>
      <div class="header-right" @click="openDirect">
        <span>打开链接</span>
      </div>
    </div>

    <!-- 内容区域 -->
    <div class="content-wrapper">
      <!-- 加载中 -->
      <div v-if="loading" class="status-container">
        <div class="loading-spinner"></div>
        <div class="status-text">加载中...</div>
      </div>

      <!-- 跳转页面（问题浏览器显示） -->
      <div v-if="showJumpPage" class="jump-container">
        <div class="jump-icon">🔗</div>
        <div class="jump-title">正在跳转...</div>
        <div class="jump-tip">如需返回请点击左上角返回按钮</div>
        <div class="jump-url">{{ targetUrl }}</div>
        <button class="jump-btn" @click="openDirect">立即访问</button>
      </div>

      <!-- 加载失败提示 -->
      <div v-if="loadFailed && !showJumpPage" class="status-container">
        <div class="error-icon">⚠️</div>
        <div class="status-title">页面无法在此显示</div>
        <div class="status-text">目标网站可能不支持嵌入显示</div>
        <button class="primary-btn" @click="openDirect">直接打开链接</button>
        <button class="secondary-btn" @click="goBack">返回上一页</button>
      </div>

      <!-- iframe -->
      <iframe
        v-show="!loading && !loadFailed"
        ref="iframe"
        :src="iframeUrl"
        class="content-iframe"
        frameborder="0"
        allowfullscreen
        sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox allow-top-navigation allow-modals"
        @load="onIframeLoad"
      ></iframe>
    </div>
  </div>
</template>

<script>
import * as API_Common from '@/api/common.js'
import { parseAppFrameLinkUrl, ensureAbsoluteHttpUrl } from '@/utils/appFrameUrl.js'
export default {
  name: 'AppFrame',
  data() {
    return {
      iframeUrl: '',
      targetUrl: '',
      lastUrl: '',
      loading: true,
      loadFailed: false,
      loadTimer: null,
      showJumpPage: false, // 显示跳转页面（问题浏览器用）
      autoJumpTimer: null, // 自动跳转定时器
      countdown: 1, // 倒计时秒数
      source: this.$route.query.source || '',
    }
  },
  created() {
    window.__TRACK_SCENE_KEY__ = 'appFrame'
    this.init()
  },
  mounted() {
    this.trackAppFrame(100068)
  },
  beforeDestroy() {
    window.__TRACK_SCENE_KEY__ = ''
    this.clearTimer()
    if (this.autoJumpTimer) {
      clearTimeout(this.autoJumpTimer)
    }
  },
  methods: {
    appFrameTrackContext() {
      return { remark: this.iframeUrl || this.targetUrl }
    },
    trackAppFrame(eventType) {
      API_Common.handleBatchTrackBase(eventType, this.appFrameTrackContext())
    },
    init() {
      // 获取来源页（用于返回）
      this.lastUrl = sessionStorage.getItem('lastUrl') || document.referrer || ''

      // 解码目标 URL（占位符还原为 #）；无协议域名补 https://
      this.targetUrl = ensureAbsoluteHttpUrl(parseAppFrameLinkUrl(this.$route.query.url || ''))

      // 验证 URL
      if (!this.targetUrl || !/^https?:\/\/.+/i.test(this.targetUrl)) {
        this.trackAppFrame(100070)
        this.loadFailed = true
        this.loading = false
        return
      }

      // ========== 关键：检测问题浏览器 ==========
      // 这些浏览器的 iframe 会显示空白，直接跳转更好
      const ua = navigator.userAgent
      const uaLower = ua.toLowerCase()
      const isProblematicBrowser =
        /360/i.test(ua) || // 360 浏览器
        uaLower.includes('qhbrowser') || // 360
        uaLower.includes('qihoo') || // 360
        uaLower.includes('huawei') || // 华为
        uaLower.includes('honor') || // 荣耀
        uaLower.includes('harmonyos') || // 鸿蒙
        uaLower.includes('hmscore') // 华为服务

      // 问题浏览器：显示跳转页面，然后自动跳转
      if (isProblematicBrowser) {
        console.log('检测到不支持 iframe 的浏览器，显示跳转页面')
        this.loading = false
        this.showJumpPage = true

        // 1秒后自动跳转（给用户时间看到返回按钮）
        this.autoJumpTimer = setTimeout(() => {
          this.openDirect()
        }, 200)
        return
      }

      this.iframeUrl = this.targetUrl
      // // 设置 iframe URL
      // this.iframeUrl = this.targetUrl

      // 超时检测：3秒后如果还在加载，显示失败提示
      this.loadTimer = setTimeout(() => {
        if (this.loading) {
          this.trackAppFrame(100070)
          this.loading = false
          this.loadFailed = true
        }
      }, 2000)
    },

    clearTimer() {
      if (this.loadTimer) {
        clearTimeout(this.loadTimer)
        this.loadTimer = null
      }
    },

    onIframeLoad() {
      this.clearTimer()

      // 检测 iframe 是否真的加载成功
      setTimeout(() => {
        try {
          const iframe = this.$refs.iframe
          if (!iframe) {
            this.trackAppFrame(100070)
            this.loadFailed = true
            this.loading = false
            return
          }

          // 尝试访问 iframe 内容（跨域会报错，这是正常的）
          const doc = iframe.contentDocument || iframe.contentWindow?.document
          if (doc) {
            const content = doc.body?.innerHTML || ''
            // 如果内容为空或很少，可能是加载失败
            if (content.trim().length < 10) {
              this.trackAppFrame(100070)
              this.loadFailed = true
              this.loading = false
              return
            }
          }
        } catch (e) {
          // 跨域错误说明确实加载了外部页面，这是正常的
          console.log('iframe 加载外部页面成功')
        }

        this.loading = false
        this.loadFailed = false
        this.trackAppFrame(100071)
      }, 500)
    },

    goBack() {
      // 清除自动跳转定时器
      if (this.autoJumpTimer) {
        clearTimeout(this.autoJumpTimer)
      }

      if (this.lastUrl) {
        this.trackAppFrame(100072)
        window.location.href = this.lastUrl
      } else if (window.history.length > 1) {
        this.trackAppFrame(100072)
        window.history.back()
      } else {
        this.trackAppFrame(100072)
        // 没有历史记录，跳转到首页
        this.$router.push('/')
      }
    },

    openDirect() {
      this.trackAppFrame(100069)
      // 直接跳转到目标页面
      window.location.href = this.targetUrl
    },
  },
}
</script>

<style scoped>
.app-frame-container {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  background: #fff;
}

/* 顶部导航 */
.header-bar {
  height: 0.88rem;
  background: #fff;
  display: flex;
  align-items: center;
  padding: 0 0.2rem;
  border-bottom: 1px solid #eee;
  flex-shrink: 0;
}

.back-button {
  display: flex;
  align-items: center;
  padding: 0.1rem 0;
  color: #333;
  font-size: 0.28rem;
}

.back-icon {
  width: 0.36rem;
  height: 0.36rem;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23333'%3E%3Cpath d='M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z'/%3E%3C/svg%3E");
  background-size: contain;
  background-repeat: no-repeat;
}

.header-title {
  flex: 1;
  text-align: center;
  font-size: 0.32rem;
  font-weight: 500;
  color: #333;
}

.header-right {
  color: #ff5612;
  font-size: 0.26rem;
  padding: 0.1rem 0;
}

/* 内容区域 */
.content-wrapper {
  flex: 1;
  position: relative;
  overflow: hidden;
}

.content-iframe {
  width: 100%;
  height: 100%;
  border: none;
}

/* 状态提示 */
.status-container {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #f8f9fa;
  padding: 0.4rem;
}

.loading-spinner {
  width: 0.7rem;
  height: 0.7rem;
  border: 3px solid #eee;
  border-top-color: #ff5612;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin-bottom: 0.3rem;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.error-icon {
  font-size: 1rem;
  margin-bottom: 0.3rem;
}

.status-title {
  font-size: 0.32rem;
  font-weight: 500;
  color: #333;
  margin-bottom: 0.15rem;
}

.status-text {
  font-size: 0.26rem;
  color: #999;
  margin-bottom: 0.4rem;
}

.primary-btn {
  width: 4rem;
  height: 0.88rem;
  background: linear-gradient(135deg, #ff5612, #ff7a3d);
  color: #fff;
  border: none;
  border-radius: 0.44rem;
  font-size: 0.3rem;
  margin-bottom: 0.2rem;
}

.secondary-btn {
  width: 4rem;
  height: 0.88rem;
  background: #fff;
  color: #666;
  border: 1px solid #ddd;
  border-radius: 0.44rem;
  font-size: 0.3rem;
}

/* 跳转页面样式 */
.jump-container {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #fff5f0 0%, #fff 100%);
  padding: 0.4rem;
}

.jump-icon {
  font-size: 1.2rem;
  margin-bottom: 0.4rem;
}

.jump-title {
  font-size: 0.36rem;
  font-weight: 500;
  color: #333;
  margin-bottom: 0.15rem;
}

.jump-tip {
  font-size: 0.24rem;
  color: #999;
  margin-bottom: 0.3rem;
}

.jump-url {
  font-size: 0.24rem;
  color: #999;
  max-width: 90%;
  word-break: break-all;
  text-align: center;
  line-height: 1.5;
  margin-bottom: 0.6rem;
  padding: 0.2rem 0.3rem;
  background: #f5f5f5;
  border-radius: 0.1rem;
}

.jump-btn {
  width: 5rem;
  height: 0.96rem;
  background: linear-gradient(135deg, #ff5612, #ff7a3d);
  color: #fff;
  border: none;
  border-radius: 0.48rem;
  font-size: 0.34rem;
  font-weight: 500;
  box-shadow: 0 4px 15px rgba(255, 86, 18, 0.3);
}

.jump-btn:active {
  opacity: 0.9;
  transform: scale(0.98);
}
</style>
